import { useMemo, useState } from 'react';
import { MediaDropRegion, profileImageUploader, type UploadItem } from '@arthelper/ui';
import { useQuery, useMutation, gql } from '@apollo/client';
import {
  Button,
  Input,
  Textarea,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Label,
} from '@artstorefronts/ui';
import { Plus, Loader2, Edit2, Archive } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import * as LucideIcons from 'lucide-react';

import AdminLayout from '../../../components/AdminLayout';

const BADGES_QUERY = gql`
  query BadgesQuery {
    badges {
      id
      name
      color
      icon
      description
      hoverText
      url
      iconOnly
      isArchived
      assignedCount
      createdAt
      thumbnailUrl
      ctaText
      ctaUrl
      ctaBgColor
      ctaTextColor
    }
  }
`;

const ADMIN_CREATE_BADGE = gql`
  mutation AdminCreateBadge($input: CreateBadgeInput!) {
    adminCreateBadge(input: $input) {
      id
      name
      color
      icon
      description
      hoverText
      url
      iconOnly
      thumbnailUrl
      ctaText
      ctaUrl
      ctaBgColor
      ctaTextColor
    }
  }
`;

const ADMIN_UPDATE_BADGE = gql`
  mutation AdminUpdateBadge($input: UpdateBadgeInput!) {
    adminUpdateBadge(input: $input) {
      id
      name
      color
      icon
      description
      hoverText
      url
      iconOnly
      isArchived
      thumbnailUrl
      ctaText
      ctaUrl
      ctaBgColor
      ctaTextColor
    }
  }
`;

const UPLOAD_BADGE_IMAGE = gql`
  mutation UploadBadgeImage($badgeId: ID!, $contentType: String) {
    uploadBadgeImage(badgeId: $badgeId, contentType: $contentType) {
      success
      url
      error
    }
  }
`;

const PROCESS_BADGE_IMAGE = gql`
  mutation ProcessBadgeImage($badgeId: ID!, $key: String!) {
    processBadgeImage(badgeId: $badgeId, key: $key) {
      success
      url
      error
    }
  }
`;

interface Badge {
  id: string;
  name: string;
  color: string;
  icon: string;
  description: string | null;
  hoverText: string | null;
  url: string | null;
  iconOnly: boolean;
  isArchived: boolean;
  assignedCount: number;
  createdAt: string;
  thumbnailUrl: string | null;
  ctaText: string | null;
  ctaUrl: string | null;
  ctaBgColor: string | null;
  ctaTextColor: string | null;
}

interface BadgeFormData {
  name: string;
  color: string;
  icon: string;
  description: string;
  hoverText: string;
  url: string;
  iconOnly: boolean;
  isArchived?: boolean;
  thumbnailUrl: string;
  ctaText: string;
  ctaUrl: string;
  ctaBgColor: string;
  ctaTextColor: string;
}

const COMMON_ICONS = [
  'Shield',
  'CheckCircle',
  'Star',
  'Award',
  'Crown',
  'Zap',
  'Heart',
  'BadgeCheck',
  'CircleCheck',
  'Sparkles',
  'Trophy',
  'Medal',
  'Flag',
  'Flame',
  'ShieldCheck',
];

export default function BadgesPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editBadge, setEditBadge] = useState<Badge | null>(null);
  const [formData, setFormData] = useState<BadgeFormData>({
    name: '',
    color: '#000000',
    icon: 'Shield',
    description: '',
    hoverText: '',
    url: '',
    iconOnly: false,
    thumbnailUrl: '',
    ctaText: '',
    ctaUrl: '',
    ctaBgColor: '#000000',
    ctaTextColor: '#ffffff',
  });

  const { data, loading, refetch } = useQuery(BADGES_QUERY, {
    fetchPolicy: 'cache-and-network',
  });

  const [adminCreateBadge, { loading: creating }] = useMutation(ADMIN_CREATE_BADGE);
  const [adminUpdateBadge, { loading: updating }] = useMutation(ADMIN_UPDATE_BADGE);
  const [uploadBadgeImage] = useMutation(UPLOAD_BADGE_IMAGE);
  const [processBadgeImage] = useMutation(PROCESS_BADGE_IMAGE);

  const badges = data?.badges || [];

  const handleOpenCreateDialog = () => {
    setFormData({
      name: '',
      color: '#000000',
      icon: 'Shield',
      description: '',
      hoverText: '',
      url: '',
      iconOnly: false,
      thumbnailUrl: '',
      ctaText: '',
      ctaUrl: '',
      ctaBgColor: '#000000',
      ctaTextColor: '#ffffff',
    });
    setCreateDialogOpen(true);
  };

  const handleOpenEditDialog = (badge: Badge) => {
    setEditBadge(badge);
    setFormData({
      name: badge.name,
      color: badge.color,
      icon: badge.icon,
      description: badge.description || '',
      hoverText: badge.hoverText || '',
      url: badge.url || '',
      iconOnly: badge.iconOnly,
      isArchived: badge.isArchived,
      thumbnailUrl: badge.thumbnailUrl || '',
      ctaText: badge.ctaText || '',
      ctaUrl: badge.ctaUrl || '',
      ctaBgColor: badge.ctaBgColor || '#000000',
      ctaTextColor: badge.ctaTextColor || '#ffffff',
    });
  };

  const handleCloseDialog = () => {
    setCreateDialogOpen(false);
    setEditBadge(null);
    setFormData({
      name: '',
      color: '#000000',
      icon: 'Shield',
      description: '',
      hoverText: '',
      url: '',
      iconOnly: false,
      thumbnailUrl: '',
      ctaText: '',
      ctaUrl: '',
      ctaBgColor: '#000000',
      ctaTextColor: '#ffffff',
    });
  };

  const badgeUploader = useMemo(
    () =>
      profileImageUploader({
        presign: async (file) => {
          if (!editBadge) throw new Error('Please create the badge first before uploading a thumbnail');
          const signed = (await uploadBadgeImage({ variables: { badgeId: editBadge.id, contentType: file.type } })).data
            ?.uploadBadgeImage;
          if (!signed?.url) throw new Error('Failed to get upload URL');
          return { url: signed.url, key: new URL(signed.url).pathname.substring(1) };
        },
        process: async (key) => {
          if (!editBadge) throw new Error('Badge is missing');
          const processed = (await processBadgeImage({ variables: { badgeId: editBadge.id, key } })).data
            ?.processBadgeImage;
          if (!processed?.url) throw new Error('Failed to process image');
          return processed.url as string;
        },
      }),
    [editBadge, processBadgeImage, uploadBadgeImage]
  );

  const existingThumbnail = useMemo(
    () =>
      formData.thumbnailUrl
        ? [
            {
              id: `badge:${formData.thumbnailUrl}`,
              url: formData.thumbnailUrl,
              kind: 'image' as const,
              name: 'Badge thumbnail',
            },
          ]
        : [],
    [formData.thumbnailUrl]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      toast.error(t('badges-page.toast.name-required'));
      return;
    }

    try {
      if (editBadge) {
        await adminUpdateBadge({
          variables: {
            input: {
              id: editBadge.id,
              name: formData.name.trim(),
              color: formData.color,
              icon: formData.icon,
              description: formData.description.trim() || null,
              hoverText: formData.hoverText.trim() || null,
              url: formData.url.trim() || null,
              iconOnly: formData.iconOnly,
              isArchived: formData.isArchived,
              thumbnailUrl: formData.thumbnailUrl.trim() || null,
              ctaText: formData.ctaText.trim() || null,
              ctaUrl: formData.ctaUrl.trim() || null,
              ctaBgColor: formData.ctaBgColor.trim() || null,
              ctaTextColor: formData.ctaTextColor.trim() || null,
            },
          },
        });
        toast.success(t('badges-page.toast.updated'));
      } else {
        await adminCreateBadge({
          variables: {
            input: {
              name: formData.name.trim(),
              color: formData.color,
              icon: formData.icon,
              description: formData.description.trim() || null,
              hoverText: formData.hoverText.trim() || null,
              url: formData.url.trim() || null,
              iconOnly: formData.iconOnly,
              thumbnailUrl: formData.thumbnailUrl.trim() || null,
              ctaText: formData.ctaText.trim() || null,
              ctaUrl: formData.ctaUrl.trim() || null,
              ctaBgColor: formData.ctaBgColor.trim() || null,
              ctaTextColor: formData.ctaTextColor.trim() || null,
            },
          },
        });
        toast.success(t('badges-page.toast.created'));
      }
      handleCloseDialog();
      refetch();
    } catch (error: any) {
      console.error('Error saving badge:', error);
      toast.error(error.message || t('badges-page.toast.save-failed'));
    }
  };

  const renderIcon = (iconName: string, className?: string) => {
    const IconComponent = (LucideIcons as any)[iconName];
    if (!IconComponent) {
      return <LucideIcons.Shield className={className} />;
    }
    return <IconComponent className={className} />;
  };

  if (loading && !data) {
    return (
      <AdminLayout title={t('badges-page.page-title')} description={t('badges-page.page-description')}>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout
      title={t('badges-page.page-title')}
      description={t('badges-page.page-description')}
      action={
        <Button onClick={handleOpenCreateDialog} className="flex items-center gap-2">
          <Plus className="w-4 h-4" />
          {t('badges-page.create-badge')}
        </Button>
      }
    >
      <div className="bg-white rounded-md border overflow-hidden">
        <div className="bg-zinc-50 border-b">
          <div className="grid grid-cols-[80px_200px_200px_1fr_120px_100px_80px] py-3 px-4 text-xs font-medium">
            <div>{t('badges-page.table.color')}</div>
            <div>{t('badges-page.table.icon')}</div>
            <div>{t('badges-page.table.name')}</div>
            <div>{t('badges-page.table.description')}</div>
            <div className="text-center">{t('badges-page.table.assigned')}</div>
            <div className="text-center">{t('badges-page.table.status')}</div>
            <div className="text-right">{t('badges-page.table.actions')}</div>
          </div>
        </div>
        <div>
          {badges.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>{t('badges-page.empty.title')}</p>
              <p className="text-sm mt-2">{t('badges-page.empty.hint')}</p>
            </div>
          ) : (
            badges.map((badge: Badge) => (
              <div key={badge.id} className="border-b last:border-b-0">
                <div className="grid grid-cols-[80px_200px_200px_1fr_120px_100px_80px] items-center px-4 py-3 text-sm hover:bg-gray-50">
                  <div>
                    <div className="w-8 h-8 rounded" style={{ backgroundColor: badge.color }} title={badge.color} />
                  </div>
                  <div className="flex items-center gap-2">
                    {renderIcon(badge.icon, 'w-5 h-5')}
                    <span className="text-xs text-gray-600">{badge.icon}</span>
                  </div>
                  <div className="font-medium">{badge.name}</div>
                  <div className="text-gray-600 truncate pr-4" title={badge.description || ''}>
                    {badge.description || '—'}
                  </div>
                  <div className="text-center">{badge.assignedCount}</div>
                  <div className="text-center">
                    {badge.isArchived ? (
                      <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                        <Archive className="w-3 h-3" />
                        {t('badges-page.status-archived')}
                      </span>
                    ) : badge.iconOnly ? (
                      <span className="text-xs text-blue-600">{t('badges-page.status-icon-only')}</span>
                    ) : (
                      <span className="text-xs text-green-600">{t('badges-page.status-active')}</span>
                    )}
                  </div>
                  <div className="flex justify-end">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleOpenEditDialog(badge)}
                      className="h-8 w-8 p-0"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <Dialog open={createDialogOpen || !!editBadge} onOpenChange={handleCloseDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editBadge ? t('badges-page.dialog.title-edit') : t('badges-page.dialog.title-create')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">{t('badges-page.form.name-label')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t('badges-page.form.name-placeholder')}
                required
              />
            </div>

            <div>
              <Label htmlFor="color">{t('badges-page.form.color-label')}</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="h-10 w-20 rounded border border-gray-200 cursor-pointer"
                />
                <Input
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  placeholder="#000000"
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="icon">{t('badges-page.form.icon-label')}</Label>
              <div className="grid grid-cols-2 gap-2">
                <select
                  id="icon"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="col-span-2 h-10 rounded-md border border-gray-200 px-3 py-2 text-sm"
                >
                  {COMMON_ICONS.map((iconName) => (
                    <option key={iconName} value={iconName}>
                      {iconName}
                    </option>
                  ))}
                </select>
                <div className="col-span-2 flex items-center gap-2 text-sm text-gray-600">
                  {t('badges-page.form.icon-preview')} {renderIcon(formData.icon, 'w-5 h-5')}
                  <span>{formData.icon}</span>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="description">{t('badges-page.form.description-label')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t('badges-page.form.description-placeholder')}
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="hoverText">{t('badges-page.form.hover-text-label')}</Label>
              <Input
                id="hoverText"
                value={formData.hoverText}
                onChange={(e) => setFormData({ ...formData, hoverText: e.target.value })}
                placeholder={t('badges-page.form.hover-text-placeholder')}
              />
            </div>

            <div>
              <Label htmlFor="url">{t('badges-page.form.url-label')}</Label>
              <Input
                id="url"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder={t('badges-page.form.url-placeholder')}
              />
              <p className="text-xs text-gray-500 mt-1">{t('badges-page.form.url-hint')}</p>
            </div>

            <div>
              <Label htmlFor="thumbnailUrl">Thumbnail Image</Label>
              {formData.thumbnailUrl && (
                <div className="mb-2">
                  <img
                    src={formData.thumbnailUrl}
                    alt="Badge thumbnail"
                    className="w-16 h-16 rounded-lg object-cover border border-gray-200"
                  />
                </div>
              )}
              {editBadge ? (
                <MediaDropRegion
                  width="100%"
                  tone="neutral"
                  prompt="Upload badge thumbnail"
                  hint="JPG, PNG, WebP · 10 MB"
                  accept={['image/jpeg', 'image/png', 'image/webp']}
                  maxSizeMB={10}
                  multiple={false}
                  existing={existingThumbnail}
                  uploader={badgeUploader}
                  onItemComplete={(item: UploadItem) => {
                    setFormData((current) => ({ ...current, thumbnailUrl: item.result as string }));
                    toast.success('Thumbnail uploaded successfully');
                    void refetch();
                  }}
                  onItemError={(_, error) => toast.error(error.message || 'Failed to upload thumbnail')}
                  onRemove={(item: UploadItem) => {
                    if (item.phase === 'done') setFormData((current) => ({ ...current, thumbnailUrl: '' }));
                  }}
                />
              ) : (
                <p className="text-xs text-gray-500">Save the badge first to upload a thumbnail</p>
              )}
            </div>

            <div>
              <Label htmlFor="ctaText">Call-to-Action Text</Label>
              <Input
                id="ctaText"
                value={formData.ctaText}
                onChange={(e) => setFormData({ ...formData, ctaText: e.target.value })}
                placeholder="e.g., Learn More"
              />
            </div>

            <div>
              <Label htmlFor="ctaUrl">Call-to-Action URL</Label>
              <Input
                id="ctaUrl"
                value={formData.ctaUrl}
                onChange={(e) => setFormData({ ...formData, ctaUrl: e.target.value })}
                placeholder="https://example.com"
              />
            </div>

            <div>
              <Label htmlFor="ctaBgColor">CTA Background Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="ctaBgColor"
                  value={formData.ctaBgColor}
                  onChange={(e) => setFormData({ ...formData, ctaBgColor: e.target.value })}
                  className="h-10 w-20 rounded border border-gray-200 cursor-pointer"
                />
                <Input
                  value={formData.ctaBgColor}
                  onChange={(e) => setFormData({ ...formData, ctaBgColor: e.target.value })}
                  placeholder="#000000"
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="ctaTextColor">CTA Text Color</Label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  id="ctaTextColor"
                  value={formData.ctaTextColor}
                  onChange={(e) => setFormData({ ...formData, ctaTextColor: e.target.value })}
                  className="h-10 w-20 rounded border border-gray-200 cursor-pointer"
                />
                <Input
                  value={formData.ctaTextColor}
                  onChange={(e) => setFormData({ ...formData, ctaTextColor: e.target.value })}
                  placeholder="#ffffff"
                  className="flex-1"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="iconOnly"
                checked={formData.iconOnly}
                onChange={(e) => setFormData({ ...formData, iconOnly: e.target.checked })}
                className="h-4 w-4 rounded border-gray-300"
              />
              <Label htmlFor="iconOnly" className="font-normal">
                {t('badges-page.form.icon-only-label')}
              </Label>
            </div>

            {editBadge && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isArchived"
                  checked={formData.isArchived}
                  onChange={(e) => setFormData({ ...formData, isArchived: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="isArchived" className="font-normal">
                  {t('badges-page.form.archive-label')}
                </Label>
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseDialog}>
                {tc('cancel')}
              </Button>
              <Button type="submit" disabled={creating || updating}>
                {creating || updating ? (
                  <div className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>{tc('saving')}</span>
                  </div>
                ) : (
                  <span>{editBadge ? t('badges-page.form.update-badge') : t('badges-page.create-badge')}</span>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
